import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import {
  CheckCircle2,
  Clock,
  Loader2,
  Mic,
  AlertCircle,
  ArrowRight
} from 'lucide-react'
import { useState } from 'react'
import { buildCallDisplayModel } from '../lib/callDisplay'
import type { EditableTodo } from '../lib/todoEditing'
import type { CallRecord, CallStatus } from '../lib/useCalls'
import { CallDetailDrawer } from './CallDetailDrawer'

interface Props {
  calls: CallRecord[]
  loading: boolean
  error: Error | null
  onRetry?: (call: CallRecord) => Promise<void>
  onUpdateTodos?: (callId: string, todos: EditableTodo[]) => Promise<void>
  onUpdateNextFollow?: (callId: string, nextFollowDate: string | null) => Promise<void>
}

const STATUS_TONE: Record<CallStatus, string> = {
  pending: 'text-gray-500 bg-gray-100',
  recording: 'text-rose-700 bg-rose-50',
  transcribing: 'text-sky-700 bg-sky-50',
  awaiting_summary: 'text-amber-700 bg-amber-50',
  summarizing: 'text-violet-700 bg-violet-50',
  complete: 'text-emerald-700 bg-emerald-50',
  failed: 'text-red-700 bg-red-50'
}

export function CallList({ calls, loading, error, onRetry, onUpdateTodos, onUpdateNextFollow }: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const selectedCall = selectedId ? calls.find((call) => call.id === selectedId) ?? null : null

  if (loading) {
    return <div className="text-sm text-gray-400 px-3 py-4">読み込み中…</div>
  }
  if (error) {
    return (
      <div className="text-sm text-red-600 px-3 py-4">
        通話履歴の取得に失敗: {String(error)}
      </div>
    )
  }
  if (calls.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-400">
        まだ通話履歴はありません。右上の「Recorderで録音」から最初の 1 件を作成できます。
      </div>
    )
  }

  return (
    <>
    <ol className="space-y-3">
      {calls.map((c) => {
        const display = buildCallDisplayModel(c)

        return (
          <li
            key={c.id}
            className="bg-white rounded-2xl border border-gray-200 overflow-hidden"
          >
            <button
              type="button"
              onClick={() => setSelectedId(c.id)}
              className="w-full text-left p-4 flex items-start gap-3 hover:bg-gray-50"
            >
              <div className="shrink-0 mt-0.5">
                <CallIcon status={c.status} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-800 text-sm">
                    {format(new Date(c.called_at), 'yyyy年M月d日 HH:mm', { locale: ja })}
                  </span>
                  {display.durationLabel && (
                    <span className="text-xs text-gray-500 tabular-nums">
                      {display.durationLabel}
                    </span>
                  )}
                  <span
                    className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${STATUS_TONE[c.status]}`}
                  >
                    {display.statusLabel}
                  </span>
                </div>
                <p className={`text-xs line-clamp-2 mt-1 ${c.status === 'failed' ? 'text-red-600' : 'text-gray-500'}`}>
                  {c.status === 'failed' ? '⚠ ' : ''}
                  {display.previewText}
                </p>
                <p className="mt-1 text-[11px] leading-snug text-gray-400 line-clamp-2">
                  {display.statusGuidance}
                </p>
              </div>
              <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-gray-300" />
            </button>
          </li>
        )
      })}
    </ol>
    <CallDetailDrawer
      call={selectedCall}
      open={selectedCall != null}
      onClose={() => setSelectedId(null)}
      onRetry={onRetry}
      onUpdateTodos={onUpdateTodos}
      onUpdateNextFollow={onUpdateNextFollow}
    />
    </>
  )
}

function CallIcon({ status }: { status: CallStatus }) {
  switch (status) {
    case 'complete':
      return <CheckCircle2 className="w-4 h-4 text-emerald-500" />
    case 'transcribing':
    case 'summarizing':
      return <Loader2 className="w-4 h-4 text-violet-500 animate-spin" />
    case 'awaiting_summary':
      return <Clock className="w-4 h-4 text-amber-500" />
    case 'failed':
      return <AlertCircle className="w-4 h-4 text-red-500" />
    default:
      return <Mic className="w-4 h-4 text-gray-400" />
  }
}
